import { FlaskConical, Pencil } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RunStatusBadge } from '@/components/hypotheses/RunStatusBadge'
import { Button } from '@/components/ui/button'
import type { HypothesisRunStatus } from '@/types/hypothesis'

export type DetailHeaderProps = {
  title: string
  /** Latest run's execution status — drives the header badge. */
  runStatus?: HypothesisRunStatus
  /** Hypothesis flagged running (covers the gap before the run row appears). */
  running: boolean
  onEdit: () => void
  /** Run actions beside Edit — *Uložiť report* (PRD-0076). */
  children?: ReactNode
}

export function DetailHeader({ title, runStatus, running, onEdit, children }: DetailHeaderProps) {
  const { t } = useTranslation()
  return (
    // Phone: icon · title · Edit on the first row, the run actions on their own row
    // under the title. From md everything sits on one row, actions before Edit.
    <div className="flex flex-wrap items-start gap-x-3 gap-y-2 md:flex-nowrap md:gap-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10 text-primary md:size-12">
        <FlaskConical className="size-5" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <h1 className="break-words text-lg font-medium tracking-tight md:text-xl">{title}</h1>
        <RunStatusBadge status={runStatus} running={running} />
      </div>
      <div className="order-last flex items-center gap-2 self-start empty:hidden max-md:basis-full max-md:pl-13 md:order-none">
        {children}
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onEdit}
        className="shrink-0 gap-1.5 self-start max-md:px-3"
      >
        <Pencil className="size-3.5" />
        {/* Phone: icon-only, so the title keeps the width (label stays for a11y). */}
        <span className="max-md:sr-only">{t('search.detail.edit')}</span>
      </Button>
    </div>
  )
}
